import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone } from 'lucide-react';
import Lottie from 'lottie-react';
import blackCatData from '../../assets/lottie/black_cat.json';
import turboRocketManData from '../../assets/lottie/turbo_rocket_man.json';
import { scrollToAppPromotion } from '../../utils/scrollToApp';
import { parseDate, formatWithSign } from '../../utils/dateUtils';
import { Game } from '../../types/api.types';

interface PlayerRecentMatchesProps {
  history: Game[];
  currentLang: 'en' | 'fr' | 'de' | 'it';
  t: {
    recentMatches: string;
    lastMatches: string;
    viewAllMatches: string;
    won: string;
    lost: string;
  };
}

/**
 * PlayerRecentMatches Component
 * Displays recent matches with dynamic Lottie animation based on performance
 */
const PlayerRecentMatches: React.FC<PlayerRecentMatchesProps> = ({
  history,
  currentLang,
  t
}) => {
  if (history.length === 0) return null;

  // Calculate win rate of last 5 matches
  const last5Matches = history.slice(0, 5);
  const wins = last5Matches.filter(game => game.win).length;
  const winRate = (wins / last5Matches.length) * 100;
  const recentMatchesLottie = winRate >= 60 ? turboRocketManData : blackCatData;

  return (
    <motion.div
      className="max-w-6xl mx-auto mb-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}>

      {/* Section Header - Standardized */}
      <div className="mb-10">
        <div className="text-center mb-6">
          {/* Lottie above title - dynamic based on performance */}
          <motion.div
            className="flex justify-center"
            style={{ marginBottom: '-40px', marginTop: '-20px' }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div style={{ width: '280px', height: '280px' }}>
              <Lottie
                animationData={recentMatchesLottie}
                loop
                style={{ width: '100%', height: '100%' }}
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid meet'
                }}
              />
            </div>
          </motion.div>
          <h2 className="text-4xl font-light text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-300">{t.recentMatches}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{t.lastMatches}</p>
        </div>
      <div className="flex justify-end">
        <motion.button
          onClick={scrollToAppPromotion}
          className="text-sm text-[#1C7AD6] dark:text-[#60A5FA] hover:text-[#1565C0] dark:hover:text-[#3B82F6] flex items-center gap-2 transition-colors duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}>
          <Smartphone className="h-4 w-4" />
          {t.viewAllMatches}
        </motion.button>
      </div>
    </div>

    <div className="space-y-2">
      {history.slice(0, 5).map((game, index) => (
        <motion.div
          key={index}
          className="flex items-center justify-between py-3 px-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-300"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors duration-300 ${
              game.win ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-rose-500 dark:bg-rose-400'
            }`} />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate transition-colors duration-300">
                {game.enemy_first_name && game.enemy_last_name
                  ? `${game.enemy_first_name} ${game.enemy_last_name}`
                  : game.opponent || 'Unknown'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate transition-colors duration-300">
                {parseDate(game.date)} • {game.competition}
              </div>
              {/* Set Score Display */}
              {game.setScore && game.setScore !== '' && (
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded transition-colors duration-300 ${
                    game.win ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  }`}>
                    {game.setScore}
                  </span>
                  {/* Individual game scores if available */}
                  {(game.s1 || game.s2 || game.s3 || game.s4 || game.s5) && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">
                      ({[game.s1, game.s2, game.s3, game.s4, game.s5].filter(s => s && s !== '').join(', ')})
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">
              {game.result || (game.win ? t.won : t.lost)}
            </div>
            <div className={`text-xs transition-colors duration-300 ${
              (game.elo_change ?? game.elo_delta ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}>
              {game.elo_change ? formatWithSign(game.elo_change) : game.elo_delta ? formatWithSign(game.elo_delta) : ''}
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </motion.div>
  );
};

export default PlayerRecentMatches;
